import React, { useEffect, useState } from "react";
import styles from "./arcana-detail.module.css";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import StarContainer from "../../components/star-container/star-container";
import Header from "../../components/header/header";
import ScrollUpButton from "../../components/buttons/scroll-up-button/scroll-up-button";
import Footer from "../../components/footer/footer";
import FullScreenModal from "../../components/savior/modal/modal-left/full-screen-modal";
import Stat from "../../components/arcana/modal/modal-right/stat";
import Event from "../../components/arcana/modal/modal-right/event";
import { active as activateFullScreen, inactive as deactivateFullScreen } from "../../store/reducers/toggle/fullScreenArcpintToggle";
import getImageFileName from "../../functions/getImageFileName";
import { changeColorTagToSpan, replaceColorTag } from "../../functions/stringFunctions";
import { selectArcanaById } from "../../store/selectors/arcanaSelectors";
import { getUIText } from "../../translation";
import { LOCALES } from "../../constants";
import { v4 } from "uuid";

const levelFlags = ["level_35", "level_40", "level_45", "level_50"];

export default function ArcanaDetail() {
    const theme = useSelector((state) => state.theme.value);
    const lang = useSelector((state) => state.lang.value);
    const dispatch = useDispatch();
    const { id } = useParams();

    const selected = useSelector((state) => selectArcanaById(state, Number(id)));

    const [levelFlag, setLevelFlag] = useState(localStorage.getItem("level_flag") || "level_50");

    useEffect(() => {
        dispatch(deactivateFullScreen());
    }, [selected, dispatch]);

    const changeLevelFlag = (flag) => {
        if (!levelFlags.includes(flag)) return;

        localStorage.setItem("level_flag", flag);
        setLevelFlag(flag);
    };

    const imgSrc = selected ? `${process.env.PUBLIC_URL}/images/cards/${getImageFileName(selected.name[LOCALES.KO])}.webp` : null;

    return (
        <div id="container" data-theme={theme} data-lang={lang}>
            {theme === "dark" && <StarContainer />}
            <Header title={getUIText(lang, "header.title.arcana")} backTo={"/arcana"} />

            {selected && (
                <div className={styles["arcana-detail-container"]}>
                    <div className={styles["level-buttons"]}>
                        {levelFlags.map((flag) => {
                            const level = parseInt(flag.replace("level_", ""));

                            return (
                                <button
                                    type="button"
                                    className={`${styles["level-button"]} ${levelFlag === flag ? styles["active"] : ""}`}
                                    onClick={() => changeLevelFlag(flag)}
                                    key={`level_flag_${level}`}
                                >
                                    Lv.{level}
                                </button>
                            );
                        })}
                    </div>

                    <div className={styles["top-row"]}>
                        <div className={styles["arcana-image"]} onClick={() => dispatch(activateFullScreen())}>
                            <img src={imgSrc} alt={selected.name[lang]} />
                        </div>
                        <div className={styles["base-info"]}>
                            <div className={styles["modal-header"]}>
                                <div className={styles["name-row"]}>
                                    <h2>{selected.name[lang]}</h2>
                                    <button
                                        type="button"
                                        className={styles["view-image-button"]}
                                        onClick={() => dispatch(activateFullScreen())}
                                    >
                                        <i className="fi fi-rr-expand" />
                                    </button>
                                </div>
                                <div className={styles["badges"]}>
                                    <span className={`${styles["rarity-badge"]} ${styles[selected.rarity]}`}>{selected.rarity}</span>
                                    <p className={styles["char-name"]}>{selected.charName[lang]}</p>
                                </div>
                            </div>
                            <div className={styles["group"]}>
                                <h3>{getUIText(lang, "arcana.card.modal.type.title")}</h3>
                                <div className={styles["badges"]}>
                                    <span className={`${styles["badge"]} ${styles["training"]}`}>{selected.mainStat[lang]}</span>
                                    {selected.assists.length > 0 && (
                                        <span className={`${styles["badge"]} ${styles["support"]}`}>{selected.assists[0][lang]}</span>
                                    )}
                                    {selected.assists.length > 0 && (
                                        <span className={`${styles["badge"]} ${styles["support"]}`}>{selected.assists[1][lang]}</span>
                                    )}
                                </div>
                            </div>
                            {(selected.spPotential || selected.uniqueEffect) && (
                                <div className={styles["group"]}>
                                    <h3>{getUIText(lang, "arcana.card.modal.uniqueAbility")}</h3>
                                    <div className={styles["group-info"]}>
                                        {selected.spPotential && (
                                            <div className={styles["box"]}>
                                                <div className={styles["title"]}>
                                                    {getUIText(lang, "arcana.card.modal.spPotential")} : {selected.spPotential.name[lang]}
                                                </div>
                                                <div
                                                    className={styles["desc"]}
                                                    dangerouslySetInnerHTML={{ __html: replaceColorTag(selected.spPotential.desc[lang]) }}
                                                ></div>
                                            </div>
                                        )}
                                        {selected.uniqueEffect && (
                                            <div className={styles["box"]}>
                                                <div className={styles["title"]}>
                                                    {getUIText(lang, "arcana.card.modal.uniqueEffect")} : {selected.uniqueEffect.name[lang]}
                                                </div>
                                                <div
                                                    className={styles["desc"]}
                                                    dangerouslySetInnerHTML={{
                                                        __html: changeColorTagToSpan(selected.uniqueEffect.desc[lang]),
                                                    }}
                                                ></div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {selected.stats.journeyStartStats.length > 0 && (
                        <div className={styles["group"]}>
                            <h3>{getUIText(lang, "arcana.card.modal.startEffect")}</h3>
                            <div className={`${styles["group-info"]} ${styles["row"]}`}>
                                {selected.stats.journeyStartStats.map((stat) => (
                                    <div className={styles["box"]} key={v4()}>
                                        <Stat lang={lang} obj={stat} levelFlag={levelFlag} isThin={false} mainStat={selected.mainStat[lang]} />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className={styles["group"]}>
                        <h3>{getUIText(lang, "arcana.card.modal.additionalEffect")}</h3>
                        <div className={`${styles["group-info"]} ${styles["grid"]}`}>
                            {selected.stats.trainingEffects.length > 0 && (
                                <div className={styles["box"]}>
                                    <div className={styles["title"]}>{getUIText(lang, "arcana.card.modal.trainingEffect")}</div>
                                    {selected.stats.trainingEffects.map((stat) => (
                                        <Stat lang={lang} obj={stat} levelFlag={levelFlag} isThin={true} key={v4()} />
                                    ))}
                                </div>
                            )}
                            {selected.stats.telepathyEffects.length > 0 && (
                                <div className={styles["box"]}>
                                    <div className={styles["title"]}>{getUIText(lang, "arcana.card.modal.telepathyEffect")}</div>
                                    {selected.stats.telepathyEffects.map((stat) => (
                                        <Stat lang={lang} obj={stat} levelFlag={levelFlag} isThin={true} key={v4()} />
                                    ))}
                                </div>
                            )}
                            {selected.stats.supportQuestEffects.length > 0 && (
                                <div className={styles["box"]}>
                                    <div className={styles["title"]}>{getUIText(lang, "arcana.card.modal.supportQuestEffect")}</div>
                                    {selected.stats.supportQuestEffects.map((stat) => (
                                        <Stat lang={lang} obj={stat} levelFlag={levelFlag} isThin={true} key={v4()} />
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className={styles["group"]}>
                        <h3>{getUIText(lang, "arcana.card.modal.event.title")}</h3>
                        <div className={styles["events"]}>
                            {selected.events.map((event) => (
                                <Event lang={lang} event={event} key={v4()} />
                            ))}
                        </div>
                    </div>
                </div>
            )}

            <FullScreenModal lang={lang} imgSrc={imgSrc} />

            <ScrollUpButton />
            <Footer />
        </div>
    );
}
